import { useEffect, useRef, useState } from 'react'
import { api, ApiError, formatAge, formatDateBR, formatDateTimeBR } from '../api'
import type { ReportDetail } from '../api'
import { MediaImage } from '../components/MediaImage'

// Prévia A4 paginada do laudo, dentro do app (a barra de abas e o menu
// continuam acessíveis). Ctrl+P imprime as duas páginas; o PDF do servidor
// é a via oficial.
export function Preview({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<ReportDetail | null>(null)
  const [page, setPage] = useState(1)
  const [error, setError] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api.reports.get(reportId).then(setDetail).catch((err) => {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar laudo')
    })
  }, [reportId])

  function goTo(p: number) {
    setPage(p)
    scrollRef.current?.scrollTo({ top: 0 })
  }

  if (!detail) {
    return <div style={{ padding: 24, font: '400 12px/1.6 var(--font-mono)', color: 'var(--text-3)' }}>{error || 'Carregando…'}</div>
  }

  const r = detail.report
  const topics = detail.template?.topics ?? []
  const photos = detail.media.filter((m) => m.media.mime.startsWith('image/') && m.link.inReport !== false)
  const total = photos.length > 0 ? 2 : 1
  const examiner = (detail.examiner ?? {}) as Record<string, string>

  const patientLine = (
    <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '3px 14px', padding: '9px 11px', border: '1px solid #d5d5d5', background: '#fafafa', font: '400 9.5px/1.5 var(--font-mono)' }}>
      <div><span style={{ color: '#777' }}>Paciente </span>{detail.patient?.name}</div>
      <div><span style={{ color: '#777' }}>Sexo </span>{detail.patient?.sex === 'F' ? 'Feminino' : detail.patient?.sex === 'M' ? 'Masculino' : '—'}</div>
      <div><span style={{ color: '#777' }}>Idade </span>{formatAge(detail.patient?.birthDate, r.examDate)}</div>
      <div><span style={{ color: '#777' }}>Data </span>{formatDateBR(r.examDate)}</div>
      <div><span style={{ color: '#777' }}>Aparelho </span>{detail.equipment || '—'}</div>
      <div><span style={{ color: '#777' }}>Examinador </span>{examiner.name || '—'}</div>
      <div><span style={{ color: '#777' }}>Documento </span>{detail.patient?.cpf || detail.patient?.doc || '—'}</div>
      <div><span style={{ color: '#777' }}>Laudo </span>{r.number}</div>
      <div><span style={{ color: '#777' }}>Convênio </span>{detail.patient?.insurance || '—'}</div>
    </div>
  )

  const frame = (pageNo: number, children: React.ReactNode) => (
    <div
      key={pageNo}
      className="a4-page"
      style={{
        display: page === pageNo ? 'flex' : 'none',
        width: 640, minHeight: 906, background: '#fff', color: '#1a1a1a',
        boxShadow: 'var(--shadow-lg)', padding: '38px 44px',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingBottom: 12, borderBottom: '2px solid #1a1a1a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {detail.clinic.logoSha ? (
            <MediaImage sha={detail.clinic.logoSha} alt="" style={{ maxWidth: 130, maxHeight: 44, objectFit: 'contain' }} />
          ) : (
            <div style={{ width: 34, height: 34, border: '1.5px solid #1a1a1a', borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 15px/1 var(--font-mono)' }}>{detail.clinic.name.trim().charAt(0).toUpperCase()}</div>
          )}
          <div>
            <div style={{ font: '600 14px/1.2 var(--font-sans)' }}>{detail.clinic.name}</div>
            {detail.clinic.lines.map((line) => (
              <div key={line} style={{ font: '400 9px/1.4 var(--font-mono)', color: '#555' }}>{line}</div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'right', font: '400 9px/1.5 var(--font-mono)', color: '#555' }}>
          Laudo Nº {r.number}
          <br />
          {formatDateBR(r.examDate)}
        </div>
      </div>

      <div style={{ marginTop: 16, textAlign: 'center', font: '600 15px/1.2 var(--font-sans)', letterSpacing: '.1em' }}>
        {r.title || r.model}
      </div>

      {patientLine}

      {children}

      <div style={{ flex: 1 }} />

      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', font: '400 8px/1.4 var(--font-mono)', color: '#888' }}>
        <span>{r.number}</span>
        <span>Página {pageNo}/{total}</span>
      </div>
    </div>
  )

  const pageFrame = (pageNo: number) => {
    if (pageNo === 1) {
      return frame(1, (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 11 }}>
          {topics.map((t) => {
            const text = (r.content[t.id] ?? '').trim()
            if (!text) return null
            return (
              <div key={t.id}>
                <div style={{ font: '600 10px/1.3 var(--font-sans)', letterSpacing: '.09em', textTransform: 'uppercase', color: '#333' }}>{t.title}</div>
                <div style={{ marginTop: 3, font: '400 10.5px/1.55 var(--font-serif)', textAlign: 'justify' }}>{text}</div>
              </div>
            )
          })}
          {topics.every((t) => !(r.content[t.id] ?? '').trim()) && (
            <div style={{ font: '400 11px/1.5 var(--font-mono)', color: '#888' }}>Nenhum tópico preenchido.</div>
          )}
        </div>
      ))
    }
    return frame(2, (
      <>
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {photos.map((m, i) => (
            <div key={m.link.id}>
              <MediaImage sha={m.media.sha256} alt={m.link.caption}
                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', border: '1px solid #cfcfcf', display: 'block' }} />
              <div style={{ marginTop: 3, font: '400 8px/1.4 var(--font-mono)', color: '#444' }}>
                Fig. {i + 1} — {m.link.caption || 'sem legenda'}
              </div>
            </div>
          ))}
          {photos.length === 0 && (
            <div style={{ gridColumn: '1 / -1', padding: 24, textAlign: 'center', font: '400 10px/1.6 var(--font-mono)', color: '#888' }}>
              Nenhuma imagem vinculada a este exame.
            </div>
          )}
        </div>

        <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 230, textAlign: 'center' }}>
            <div style={{ height: 44, borderBottom: '1px solid #1a1a1a', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', font: 'italic 400 19px/1 var(--font-serif)', color: '#2a3f52', paddingBottom: 2 }}>
              {shortSignature(examiner.name)}
            </div>
            <div style={{ marginTop: 5, font: '500 9.5px/1.4 var(--font-mono)' }}>{examiner.name || '—'}</div>
            <div style={{ font: '400 9px/1.4 var(--font-mono)', color: '#666' }}>
              {examiner.crm ? `CRM-${examiner.crm_uf} ${examiner.crm}` : ''}{examiner.specialty ? ` · ${examiner.specialty}` : ''}
            </div>
          </div>
        </div>

        {r.releasedAt && (
          <div style={{ marginTop: 16, textAlign: 'center', font: '400 8px/1.4 var(--font-mono)', color: '#4a7a5f' }}>
            Laudo liberado eletronicamente em {formatDateTimeBR(r.releasedAt)}.
          </div>
        )}
      </>
    ))
  }

  return (
    <div className="print-flow" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface-3)' }}>
      <div className="no-print" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button className="btn" onClick={onClose} style={{ height: 28 }}>Fechar</button>
        <button className="btn btn-primary" onClick={() => void api.reports.openPdf(r.id).catch(() => setError('falha ao gerar PDF'))} style={{ height: 28 }}>
          Imprimir / PDF
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 8 }}>
          <button className="btn" onClick={() => goTo(1)}
            style={{ height: 28, fontWeight: 600, background: page === 1 ? 'var(--primary-soft)' : 'var(--surface)', color: page === 1 ? 'var(--primary)' : 'var(--text-2)' }}>
            1 · Texto
          </button>
          <button className="btn" onClick={() => goTo(2)} disabled={total < 2}
            style={{ height: 28, fontWeight: 600, background: page === 2 ? 'var(--primary-soft)' : 'var(--surface)', color: page === 2 ? 'var(--primary)' : 'var(--text-2)', opacity: total < 2 ? 0.5 : 1 }}>
            2 · Imagens ({photos.length})
          </button>
        </div>
        <button className="btn" onClick={() => goTo(Math.max(1, page - 1))} disabled={page === 1} style={{ height: 28, width: 28, padding: 0 }}>‹</button>
        <span style={{ font: '500 11.5px/1 var(--font-mono)', color: 'var(--text-2)' }}>Página {page} de {total}</span>
        <button className="btn" onClick={() => goTo(Math.min(total, page + 1))} disabled={page === total} style={{ height: 28, width: 28, padding: 0 }}>›</button>
      </div>

      {error && <div style={{ padding: '8px 12px', background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12 }}>{error}</div>}

      <div ref={scrollRef} className="print-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 22 }}>
        <div id="print-area" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {Array.from({ length: total }, (_, i) => pageFrame(i + 1))}
        </div>
      </div>
    </div>
  )
}

function shortSignature(name?: string): string {
  if (!name) return '—'
  const parts = name.trim().split(/\s+/)
  if (parts.length < 2) return name
  const initials = parts.slice(1).map((p) => p[0]?.toUpperCase() + '.').join('')
  return `${initials} ${parts[parts.length - 1]}`
}
